import React, { useEffect, useState } from "react";
import { EyeOutlined } from "@ant-design/icons";
import { Button, message } from "antd";
import GenericTable from "../../../Components/Tables/GenericTable";
import { useNavigate } from "react-router-dom";
import { getFuelVendors } from "../../../API/Admin/FuelVendors";
import "./style.css";
import { getInitials } from "../../../Components/InitialsAvatar";

const AVATAR_COLORS = [
  "#4F46E5",
  "#EA580C",
  "#16A34A",
  "#DC2626",
  "#2563EB",
  "#7C3AED",
];
const getAvatarColor = (name) =>
  AVATAR_COLORS[(name || "A").charCodeAt(0) % AVATAR_COLORS.length];

const BACKEND_ORIGIN = process.env.REACT_APP_BACKEND_URL?.replace(
  /\/?api\/?$/,
  "",
);

const resolveAvatarUrl = (avatar) => {
  if (!avatar) return null;
  if (/^https?:\/\//i.test(avatar) || avatar.startsWith("blob:")) {
    return avatar;
  }
  return `${BACKEND_ORIGIN}${avatar.startsWith("/") ? "" : "/"}${avatar}`;
};

const capitalize = (str) =>
  str ? str.charAt(0).toUpperCase() + str.slice(1) : str;

const getErrorMessage = (data, fallback) =>
  data?.message || data?.detail || fallback;

const formatNumber = (val) => {
  const num = parseFloat(val);
  return Number.isNaN(num)
    ? "0.00"
    : num.toLocaleString("en-US", { minimumFractionDigits: 2 });
};

const mapVendor = (item) => ({
  key: String(item.id),
  company: item.legal_name || "-",
  avatar: resolveAvatarUrl(item.avatar),
  total_fuel_volume: `${formatNumber(item.total_fuel_volume)} L`,
  total_fuel_cost: `$${formatNumber(item.total_fuel_cost)}`,
  outstanding_balance: `$${formatNumber(item.outstanding_balance)}`,
  status: capitalize(item.status) || "-",
});

const FuelVendors = () => {
  const navigate = useNavigate();

  const [dataSource, setDataSource] = useState([]);

  useEffect(() => {
    const fetchVendors = async () => {
      try {
        const res = await getFuelVendors();
        const vendors = res?.data?.data;

        if (Math.floor(res?.status / 100) === 2 && Array.isArray(vendors)) {
          setDataSource(vendors.map(mapVendor));
        } else {
          message.error(
            getErrorMessage(res?.data, "Failed to load fuel vendors."),
          );
        }
      } catch (err) {
        message.error(
          getErrorMessage(err?.response?.data, "Failed to load fuel vendors."),
        );
      }
    };

    fetchVendors();
  }, []);

  const uniqueStatuses = [
    ...new Set(dataSource.map((item) => item.status).filter((v) => v !== "-")),
  ];

  const statusFilters = uniqueStatuses.map((s) => ({
    text: s,
    value: s,
  }));

  const columns = [
    {
      title: "Vendor",
      dataIndex: "company",
      key: "company",
      enableSearch: true,
      render: (text, record) => (
        <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {record.avatar ? (
            <img
              src={record.avatar}
              alt={text}
              className="vendor-avatar-circle"
              style={{ objectFit: "cover" }}
            />
          ) : (
            <span
              className="vendor-avatar-circle"
              style={{ background: getAvatarColor(text) }}
            >
              {getInitials(text)}
            </span>
          )}
          {text}
        </span>
      ),
    },
    {
      title: "Total Fuel Volume",
      dataIndex: "total_fuel_volume",
      key: "total_fuel_volume",
      sorter: (a, b) =>
        parseFloat(a.total_fuel_volume.replace(/[^0-9.]/g, "")) -
        parseFloat(b.total_fuel_volume.replace(/[^0-9.]/g, "")),
    },
    {
      title: "Total Fuel Cost",
      dataIndex: "total_fuel_cost",
      key: "total_fuel_cost",
      sorter: (a, b) =>
        parseFloat(a.total_fuel_cost.replace(/[^0-9.]/g, "")) -
        parseFloat(b.total_fuel_cost.replace(/[^0-9.]/g, "")),
    },
    {
      title: "Outstanding Balance",
      dataIndex: "outstanding_balance",
      key: "outstanding_balance",
      sorter: (a, b) =>
        parseFloat((a.outstanding_balance || "$0").replace(/[^0-9.-]/g, "")) -
        parseFloat((b.outstanding_balance || "$0").replace(/[^0-9.-]/g, "")),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      filters: statusFilters,
      onFilter: (value, record) => record.status === value,
      render: (status) => (
        <span
          className={`status-badge ${status === "Active" ? "active" : "locked"}`}
        >
          {status}
        </span>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Button
          type="text"
          icon={<EyeOutlined style={{ fontSize: 18, color: "#4F46E5" }} />}
          onClick={() =>
            navigate("/vendor-detail", {
              state: { id: record.key, from: "fuel-vendors-list" },
            })
          }
        />
      ),
    },
  ];

  return (
    <div className="vendor-table">
      <GenericTable
        title="Fuel Vendors"
        dataSource={dataSource}
        columns={columns}
        enableRowSelection={false}
      />
    </div>
  );
};

export default FuelVendors;
